import type { AxiosError, AxiosRequestConfig } from "axios";

const REDACTED_VALUE = "[REDACTED]";
const MAX_STRING_LENGTH = 2_000;
const MAX_SERIALIZED_LENGTH = 12_000;
const MAX_OBJECT_DEPTH = 6;
const MAX_ARRAY_ITEMS = 25;
const MAX_OBJECT_KEYS = 50;
const MAX_PENDING_LOGS = 20;
const MAX_PENDING_SESSION_EXPIRATION_LOGS = 20;

const SENSITIVE_KEY_PATTERN =
  /(authorization|cookie|credential|password|senha|secret|token|api[_-]?key|access[_-]?token|refresh[_-]?token|bearer|cpf|cnpj|rg|document|e-?mail|telefone|phone|fone|name|nome|address|endere[cç]o|logradouro|bairro|cep|birth|nascimento|account|conta|agency|ag[eê]ncia|card|cart[aã]o|renda|income|sal[aá]rio|salary|base64|binary|filecontent|selfie|photo|foto|image|imagem)/i;
const BEARER_TOKEN_PATTERN = /Bearer\s+[^\s,;]+/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const BRAZILIAN_DOCUMENT_PATTERN =
  /\b(?:\d{3}\.?\d{3}\.?\d{3}-?\d{2}|\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})\b/g;
const PAYMENT_CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;
const BRAZILIAN_PHONE_PATTERN =
  /\b(?:\+?55\s*)?(?:\(?\d{2}\)?\s*)?9?\d{4}[-\s]?\d{4}\b/g;
const LONG_ENCODED_VALUE_PATTERN = /^[A-Za-z0-9+/_=-]+$/;

export type ApiClientName = "api" | "new_api" | "wayup_api";

export type RequestErrorLogAttributes = {
  api_client: ApiClientName;
  http_method: string;
  request_url: string;
  response_status: number;
  error_code?: string;
  error_message?: string;
  request_headers?: string;
  request_params?: string;
  request_body?: string;
  response_body?: string;
};

export type SessionExpirationLogAttributes = {
  api_client: ApiClientName;
  logout_reason: "expired_session_401";
  http_method: string;
  request_url: string;
  response_status: number;
  response_body?: string;
};

type RequestErrorLogHandler = (attributes: RequestErrorLogAttributes) => void;
type SessionExpirationLogHandler = (
  attributes: SessionExpirationLogAttributes
) => void;

let requestErrorLogHandler: RequestErrorLogHandler | null = null;
let sessionExpirationLogHandler: SessionExpirationLogHandler | null = null;
const pendingLogs: RequestErrorLogAttributes[] = [];
const pendingSessionExpirationLogs: SessionExpirationLogAttributes[] = [];

function deliverLog<TAttributes>(
  handler: (attributes: TAttributes) => void,
  attributes: TAttributes
): void {
  try {
    handler(attributes);
  } catch {
    // Observability must never replace or interrupt the original request error.
  }
}

function truncate(value: string, maxLength = MAX_STRING_LENGTH): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}…[TRUNCATED ${
    value.length - maxLength
  } chars]`;
}

function sanitizeString(value: string): string {
  const redacted = value
    .replace(BEARER_TOKEN_PATTERN, "Bearer [REDACTED]")
    .replace(EMAIL_PATTERN, REDACTED_VALUE)
    .replace(BRAZILIAN_DOCUMENT_PATTERN, REDACTED_VALUE)
    .replace(PAYMENT_CARD_PATTERN, REDACTED_VALUE)
    .replace(BRAZILIAN_PHONE_PATTERN, REDACTED_VALUE);

  if (
    redacted.length > 200 &&
    LONG_ENCODED_VALUE_PATTERN.test(redacted.replace(/\s/g, ""))
  ) {
    return `[REDACTED_BINARY length=${redacted.length}]`;
  }

  return truncate(redacted);
}

function parseJsonString(value: string): unknown {
  const trimmed = value.trim();

  if (
    (!trimmed.startsWith("{") || !trimmed.endsWith("}")) &&
    (!trimmed.startsWith("[") || !trimmed.endsWith("]"))
  ) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function sanitizeValue(
  value: unknown,
  key = "",
  depth = 0,
  seen = new WeakSet<object>()
): unknown {
  if (SENSITIVE_KEY_PATTERN.test(key)) {
    return REDACTED_VALUE;
  }

  if (
    value === null ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = parseJsonString(value);
    return parsedValue === value
      ? sanitizeString(value)
      : sanitizeValue(parsedValue, key, depth, seen);
  }

  if (typeof value === "undefined") {
    return undefined;
  }

  if (typeof value !== "object") {
    return sanitizeString(String(value));
  }

  if (depth >= MAX_OBJECT_DEPTH) {
    return "[MAX_DEPTH]";
  }

  if (seen.has(value)) {
    return "[CIRCULAR]";
  }

  seen.add(value);

  if (Array.isArray(value)) {
    const sanitizedItems = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeValue(item, key, depth + 1, seen));

    if (value.length > MAX_ARRAY_ITEMS) {
      sanitizedItems.push(
        `[TRUNCATED ${value.length - MAX_ARRAY_ITEMS} items]`
      );
    }

    return sanitizedItems;
  }

  const serializableValue =
    "toJSON" in value && typeof value.toJSON === "function"
      ? value.toJSON()
      : value;

  if (serializableValue !== value) {
    return sanitizeValue(serializableValue, key, depth + 1, seen);
  }

  const entries = Object.entries(value).slice(0, MAX_OBJECT_KEYS);
  const sanitizedObject: Record<string, unknown> = {};

  entries.forEach(([entryKey, entryValue]) => {
    const sanitizedEntry = sanitizeValue(entryValue, entryKey, depth + 1, seen);

    if (sanitizedEntry !== undefined) {
      sanitizedObject[entryKey] = sanitizedEntry;
    }
  });

  if (Object.keys(value).length > MAX_OBJECT_KEYS) {
    sanitizedObject._truncated = `${
      Object.keys(value).length - MAX_OBJECT_KEYS
    } keys omitted`;
  }

  return sanitizedObject;
}

export function sanitizeRequestLogValue(value: unknown): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  const sanitizedValue = sanitizeValue(value);
  const serialized =
    typeof sanitizedValue === "string"
      ? sanitizedValue
      : JSON.stringify(sanitizedValue);

  return truncate(serialized, MAX_SERIALIZED_LENGTH);
}

function serializeRequestLogBody(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    if (
      typeof value === "object" &&
      "toJSON" in value &&
      typeof value.toJSON === "function"
    ) {
      return JSON.stringify(value.toJSON());
    }

    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function getRequestHeaders(headers: AxiosRequestConfig["headers"]): unknown {
  if (headers && "toJSON" in headers && typeof headers.toJSON === "function") {
    return headers.toJSON();
  }

  return headers;
}

function getRequestUrl(config?: AxiosRequestConfig): string {
  const path = (config?.url || "").split("?")[0];
  const baseUrl = config?.baseURL?.replace(/\/$/, "") || "";
  const normalizedPath = path && !path.startsWith("/") ? `/${path}` : path;

  return `${baseUrl}${normalizedPath}`;
}

export function createRequestErrorLog(
  apiClient: ApiClientName,
  error: unknown
): RequestErrorLogAttributes {
  const axiosError = error as AxiosError;
  const config = axiosError.config;
  const status = axiosError.response?.status;
  const code = axiosError.code;
  const message = sanitizeRequestLogValue(axiosError.message);
  const headers = sanitizeRequestLogValue(getRequestHeaders(config?.headers));
  const params = sanitizeRequestLogValue(config?.params);
  const requestBody = serializeRequestLogBody(config?.data);
  const responseBody = serializeRequestLogBody(axiosError.response?.data);

  return {
    api_client: apiClient,
    http_method: config?.method?.toUpperCase() || "UNKNOWN",
    request_url: getRequestUrl(config),
    response_status: status ?? 0,
    ...(code ? { error_code: code } : {}),
    ...(message ? { error_message: message } : {}),
    ...(headers ? { request_headers: headers } : {}),
    ...(params ? { request_params: params } : {}),
    ...(requestBody ? { request_body: requestBody } : {}),
    ...(responseBody ? { response_body: responseBody } : {})
  };
}

export function createSessionExpirationLog(
  apiClient: ApiClientName,
  error: unknown
): SessionExpirationLogAttributes {
  const requestLog = createRequestErrorLog(apiClient, error);
  const axiosError = error as AxiosError;
  const responseBody = sanitizeRequestLogValue(axiosError.response?.data);

  return {
    api_client: requestLog.api_client,
    logout_reason: "expired_session_401",
    http_method: requestLog.http_method,
    request_url: requestLog.request_url,
    response_status: requestLog.response_status,
    ...(responseBody ? { response_body: responseBody } : {})
  };
}

export function registerRequestErrorLogHandler(
  handler: RequestErrorLogHandler
): () => void {
  requestErrorLogHandler = handler;

  pendingLogs.splice(0).forEach((attributes) => {
    deliverLog(handler, attributes);
  });

  return () => {
    if (requestErrorLogHandler === handler) {
      requestErrorLogHandler = null;
    }
  };
}

export function registerSessionExpirationLogHandler(
  handler: SessionExpirationLogHandler
): () => void {
  sessionExpirationLogHandler = handler;

  pendingSessionExpirationLogs.splice(0).forEach((attributes) => {
    deliverLog(handler, attributes);
  });

  return () => {
    if (sessionExpirationLogHandler === handler) {
      sessionExpirationLogHandler = null;
    }
  };
}

export function logAxiosRequestError(
  apiClient: ApiClientName,
  error: unknown
): void {
  let attributes: RequestErrorLogAttributes;

  try {
    attributes = createRequestErrorLog(apiClient, error);
  } catch {
    return;
  }

  if (requestErrorLogHandler) {
    deliverLog(requestErrorLogHandler, attributes);
    return;
  }

  if (pendingLogs.length >= MAX_PENDING_LOGS) {
    pendingLogs.shift();
  }

  pendingLogs.push(attributes);
}

export function logAxiosSessionExpiration(
  apiClient: ApiClientName,
  error: unknown
): void {
  let attributes: SessionExpirationLogAttributes;

  try {
    attributes = createSessionExpirationLog(apiClient, error);
  } catch {
    return;
  }

  if (sessionExpirationLogHandler) {
    deliverLog(sessionExpirationLogHandler, attributes);
    return;
  }

  if (
    pendingSessionExpirationLogs.length >= MAX_PENDING_SESSION_EXPIRATION_LOGS
  ) {
    pendingSessionExpirationLogs.shift();
  }

  pendingSessionExpirationLogs.push(attributes);
}
